import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  contentChild,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  computed,
  ViewEncapsulation
} from '@angular/core';
import { UmssTableComponent } from '../table/table.component';
import { UmssPaginationComponent } from '../pagination/pagination.component';
import { UmssEmptyStateComponent } from '../empty-state/empty-state.component';
import { UmssSkeletonComponent } from '../skeleton/skeleton.component';
import { UmssDataViewRowsDirective } from './data-view-rows.directive';
import { UmssDataViewCardsDirective } from './data-view-cards.directive';
import {
  DataViewColumn,
  DataViewDataSource,
  DataViewMode,
  DataViewSortEvent
} from './data-view.model';
import { SortDirection } from '../../models/sort.model';

@Component({
  selector: 'umss-data-view',
  standalone: true,
  imports: [
    UmssTableComponent,
    UmssPaginationComponent,
    UmssEmptyStateComponent,
    UmssSkeletonComponent
  ],
  templateUrl: './data-view.component.html',
  styleUrl: './data-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class UmssDataViewComponent implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private resizeObserver?: ResizeObserver;

  // ── Inputs ──────────────────────────────────────────────────────────────────

  /** Column definitions used to render the table header. */
  readonly columns = input<DataViewColumn[]>([]);

  /**
   * Data source managed by the parent. The parent is responsible for updating
   * this (items, totalItems, loading, currentPage, pageSize) whenever a page
   * change or filter event occurs.
   */
  readonly dataSource = input<DataViewDataSource>({
    items: [],
    totalItems: 0,
    loading: false,
    currentPage: 1,
    pageSize: 10
  });

  /**
   * Which view to render. The parent is responsible for toggling this.
   * Defaults to `'table'`.
   */
  readonly viewMode = input<DataViewMode>('table');

  // ── Outputs ─────────────────────────────────────────────────────────────────

  /**
   * Emits the requested page number after the pagination debounce resolves.
   * The parent should fetch data for that page and update `dataSource`.
   */
  readonly pageChange = output<number>();

  /**
   * Emits immediately when the user clicks a page button, before the debounce.
   * The parent can use this to show a loading state right away.
   */
  readonly pageChangeStart = output<void>();

  /**
   * Emits whenever the user clicks a sortable column header.
   * `direction` cycles: null → 'asc' → 'desc' → null.
   */
  readonly sort = output<DataViewSortEvent>();

  // ── Content projection detection ────────────────────────────────────────────

  /** Detects whether a `[umssDataViewRows]` slot has been provided. */
  readonly rowsContent = contentChild(UmssDataViewRowsDirective);
  /** Detects whether a `[umssDataViewCards]` slot has been provided. */
  readonly cardsContent = contentChild(UmssDataViewCardsDirective);

  // ── Internal state ───────────────────────────────────────────────────────────

  /** Current pixel width of the host element; fed to column `hidden` fns. */
  readonly containerWidth = signal(0);

  /**
   * Number of skeleton rows/cards to show while loading.
   * Starts at 15 (first load); updated to actual item count after each load.
   */
  readonly previousItemCount = signal(15);

  readonly sortColumn = signal<string | null>(null);
  readonly sortDirection = signal<SortDirection>(null);

  // ── Computed ─────────────────────────────────────────────────────────────────

  readonly hasRowsContent = computed(() => !!this.rowsContent());
  readonly hasCardsContent = computed(() => !!this.cardsContent());

  readonly isTableView = computed(() => this.viewMode() === 'table');
  readonly isGridView = computed(() => this.viewMode() === 'grid');

  /** Columns whose `hidden` function does not hide them at the current width. */
  readonly visibleColumns = computed(() => {
    const width = this.containerWidth();
    return this.columns().filter((col) => !col.hidden?.(width));
  });

  readonly totalPages = computed(() => {
    const ds = this.dataSource();
    return Math.max(1, Math.ceil(ds.totalItems / ds.pageSize));
  });

  /** Array used to @for-render skeleton placeholders. */
  readonly skeletonRows = computed(() =>
    Array(this.previousItemCount()).fill(0)
  );

  constructor() {
    // Track item count so skeletons match the previous page size
    effect(() => {
      const ds = this.dataSource();
      if (!ds.loading && ds.items.length > 0) {
        this.previousItemCount.set(ds.items.length);
      }
    });
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.containerWidth.set(entry.contentRect.width);
      }
    });
    this.resizeObserver.observe(this.hostRef.nativeElement);
    this.destroyRef.onDestroy(() => this.resizeObserver?.disconnect());
  }

  // ── Event handlers ───────────────────────────────────────────────────────────

  /** Called by pagination `(pageChangeStart)` — notifies parent immediately. */
  onPageChangeStart(): void {
    this.pageChangeStart.emit();
  }

  /** Called by pagination `(pageChange)` — delegates to the parent. */
  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  onSort(key: string): void {
    const currentCol = this.sortColumn();
    const currentDir = this.sortDirection();

    let newDir: SortDirection;
    if (currentCol !== key) {
      newDir = 'asc';
    } else if (currentDir === 'asc') {
      newDir = 'desc';
    } else {
      newDir = null;
    }

    this.sortColumn.set(newDir ? key : null);
    this.sortDirection.set(newDir);
    this.sort.emit({ column: key, direction: newDir });
  }

  getSortIconClass(key: string): string {
    const dir = this.sortDirection();
    if (this.sortColumn() !== key || !dir) return 'sort-icon';
    return `sort-icon ${dir}`;
  }
}
